import { useQuery, useQueries, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { campusService, musterService, billingService, buildingService, zoneService, gateService, userService, studentService, classService, visitorService, tenantService, dashboardService, hseService, lookupService, exportService, preferencesService } from '@/services';
import { ertService } from '@/services/ert.service';
import type { CoverageAssignmentsQuery, DelegationsQuery, CreateCoverageAssignmentRequest } from '@/services/ert.service';
import { notificationsService, type InboxListQuery } from '@/services/notifications.service';
import { rbacService } from '@/services/rbac.service';
import type { ApiResponse, CreateAssignmentRequest, CreateExportRequest, ExportListQuery, PaginationMeta, UserNotification, HubFeedQuery, HubWidgetQuery, HubInteractionRequest } from '@amuster/types';
import type {
  CreateManagementReviewItemPayload,
  UpdateManagementReviewItemPayload,
} from '@/lib/serializeManagementReviewItemPayload';
import { hubService } from '@/services/hub.service';
import type {
  CampusListQuery,
  SessionListQuery,
  BuildingListQuery,
  ZoneListQuery,
  UserListQuery,
  StudentListQuery,
  VisitorListQuery,
  CreateCampusRequest,
  UpdateCampusRequest,
  CreateBuildingRequest,
  UpdateBuildingRequest,
  CreateZoneRequest,
  UpdateZoneRequest,
  GateListQuery,
  CreateGateRequest,
  UpdateGateRequest,
  CreateUserRequest,
  UpdateUserRequest,
  CreateStudentRequest,
  UpdateStudentRequest,
  CreateVisitorRequest,
  UpdateVisitorRequest,
  CreateMusterSessionRequest,
  UpdateStudentCheckinRequest,
  UpdateStaffCheckinRequest,
  BulkStudentCheckinRequest,
  BulkStaffCheckinRequest,
  UpdateZoneSweepRequest,
  CreateIncidentRequest,
  UpdateIncidentRequest,
  IncidentListQuery,
  StartInvestigationRequest,
  TransitionReasonRequest,
  CreateInjuredPersonRequest,
  UpdateInjuredPersonRequest,
  CreateWitnessRequest,
  UpdateWitnessRequest,
  CreateFindingRequest,
  UpdateFindingRequest,
  CreateEvidenceRequest,
  CreateNearMissRequest,
  UpdateNearMissRequest,
  NearMissListQuery,
  ReviewNearMissRequest,
  CreateCapaRequest,
  UpdateCapaRequest,
  CapaListQuery,
  CapaStatus,
  AssignCapaRequest,
  VerifyCapaRequest,
  SubmitEffectivenessReviewRequest,
  CancelCapaRequest,
  AttachCapaEvidenceRequest,
  AddCapaCommentRequest,
  EditCapaCommentRequest,
} from '@amuster/types';

// ── Campus Hooks ─────────────────────────────────────────────────────────────

export function useCampuses(query?: CampusListQuery) {
  return useQuery({
    queryKey: ['campuses', query],
    queryFn: () => campusService.list(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useCampus(id: string) {
  return useQuery({
    queryKey: ['campus', id],
    queryFn: () => campusService.getById(id).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCampusDashboard(id: string) {
  return useQuery({
    queryKey: ['campus-dashboard', id],
    queryFn: () => campusService.getDashboard(id).then((r) => r.data),
    enabled: !!id,
    refetchInterval: 30000,
  });
}

export function useCreateCampus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCampusRequest) => campusService.create(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['campuses'] }); },
  });
}

export function useUpdateCampus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampusRequest }) => campusService.update(id, data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['campuses'] }); },
  });
}

// ── Building Hooks ───────────────────────────────────────────────────────────

export function useBuildings(query?: BuildingListQuery, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['buildings', query],
    queryFn: () => buildingService.list(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
    enabled: options?.enabled,
  });
}

export function useCreateBuilding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBuildingRequest) => buildingService.create(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['buildings'] }); },
  });
}

export function useUpdateBuilding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBuildingRequest }) => buildingService.update(id, data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['buildings'] }); },
  });
}

// ── Zone Hooks ───────────────────────────────────────────────────────────────

export function useZones(query?: ZoneListQuery, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['zones', query],
    queryFn: () => zoneService.list(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
    enabled: options?.enabled,
  });
}

export function useCreateZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateZoneRequest) => zoneService.create(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['zones'] }); },
  });
}

export function useUpdateZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateZoneRequest }) => zoneService.update(id, data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['zones'] }); },
  });
}

// ── Lookup Hooks ────────────────────────────────────────────────────────────

export function useLookupValues(category: string, options?: { include_inactive?: boolean }) {
  return useQuery({
    queryKey: ['lookup', category, options],
    queryFn: () => lookupService.listByCategory(category, options).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useZoneTypes(options?: { include_inactive?: boolean }) {
  return useLookupValues('zone_type', options);
}

export function useBuildingTypes(options?: { include_inactive?: boolean }) {
  return useLookupValues('building_type', options);
}

export function useCreateLookupValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ category, data }: { category: string; data: { code: string; label_en: string; description?: string; sort_order?: number; is_active?: boolean } }) =>
      lookupService.create(category, data).then((r) => r.data),
    onSuccess: (_, variables) => { qc.invalidateQueries({ queryKey: ['lookup', variables.category] }); },
  });
}

export function useUpdateLookupValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, category, data }: { id: string; category: string; data: { label_en?: string; description?: string | null; sort_order?: number; is_active?: boolean } }) =>
      lookupService.update(id, data).then((r) => r.data),
    onSuccess: (_, variables) => { qc.invalidateQueries({ queryKey: ['lookup', variables.category] }); },
  });
}

export function useRemoveLookupValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, category }: { id: string; category: string }) =>
      lookupService.remove(id).then((r) => r.data),
    onSuccess: (_, variables) => { qc.invalidateQueries({ queryKey: ['lookup', variables.category] }); },
  });
}

// ── Gate Hooks ──────────────────────────────────────────────────────────────

export function useGates(query?: GateListQuery) {
  return useQuery({
    queryKey: ['gates', query],
    queryFn: () => gateService.list(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useGate(id: string) {
  return useQuery({
    queryKey: ['gate', id],
    queryFn: () => gateService.getById(id).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateGate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateGateRequest) => gateService.create(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gates'] }); },
  });
}

export function useUpdateGate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGateRequest }) => gateService.update(id, data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gates'] }); },
  });
}

export function useDeleteGate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gateService.remove(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gates'] }); },
  });
}

// ── User Hooks ───────────────────────────────────────────────────────────────

export function useUsers(query?: UserListQuery) {
  return useQuery({
    queryKey: ['users', query],
    queryFn: () => userService.list(query).then((r) => r.data),
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => userService.getById(id).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserRequest) => userService.create(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) => userService.update(id, data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

// ── Student Hooks ────────────────────────────────────────────────────────────

export function useStudents(query?: StudentListQuery) {
  return useQuery({
    queryKey: ['students', query],
    queryFn: () => studentService.list(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useStudent(id: string) {
  return useQuery({
    queryKey: ['student', id],
    queryFn: () => studentService.getById(id).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateStudentRequest) => studentService.create(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['students'] }); },
  });
}

export function useUpdateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStudentRequest }) => studentService.update(id, data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['students'] }); },
  });
}

// ── Class Hooks ──────────────────────────────────────────────────────────────

export function useClasses(query?: { campus_id?: string; building_id?: string; grade_level?: string; limit?: number }) {
  return useQuery({
    queryKey: ['classes', query],
    queryFn: () => classService.list(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useCreateClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { campus_id: string; building_id: string; grade_level: string; section: string; room_number?: string; floor_number?: number; homeroom_teacher_id?: string; zone_id?: string; academic_year?: string }) =>
      classService.create(data as unknown as import('@amuster/types').CreateClassRequest).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['classes'] }); },
  });
}

// ── Visitor Hooks ────────────────────────────────────────────────────────────

export function useVisitors(query?: VisitorListQuery) {
  return useQuery({
    queryKey: ['visitors', query],
    queryFn: () => visitorService.list(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useCreateVisitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateVisitorRequest) => visitorService.create(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['visitors'] }); },
  });
}

export function useVisitorCheckin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => visitorService.checkin(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['visitors'] }); },
  });
}

export function useVisitorCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => visitorService.checkout(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['visitors'] }); },
  });
}

// ── Muster Session Hooks ────────────────────────────────────────────────────

export function useMusterSessions(query?: SessionListQuery) {
  return useQuery({
    queryKey: ['muster-sessions', query],
    queryFn: () => musterService.listSessions(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useActiveMusterSessions() {
  return useQuery({
    queryKey: ['muster-sessions', 'active'],
    queryFn: () => musterService.getActiveSessions().then((r) => r.data),
    refetchInterval: 10000,
  });
}

export function useMusterSession(id: string) {
  return useQuery({
    queryKey: ['muster-session', id],
    queryFn: () => musterService.getSession(id).then((r) => r.data),
    enabled: !!id,
    refetchInterval: 5000,
  });
}

export function useMusterStats(id: string) {
  return useQuery({
    queryKey: ['muster-stats', id],
    queryFn: () => musterService.getStats(id).then((r) => r.data),
    enabled: !!id,
    refetchInterval: 5000,
  });
}

export function useMusterTimeline(id: string) {
  return useQuery({
    queryKey: ['muster-timeline', id],
    queryFn: () => musterService.getTimeline(id).then((r) => r.data),
    enabled: !!id,
    refetchInterval: 10000,
  });
}

export function useCreateMusterSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMusterSessionRequest) => musterService.createSession(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['muster-sessions'] }); },
  });
}

export function useCloseMusterSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => musterService.closeSession(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['muster-sessions'] }); },
  });
}

// ── Checkin Hooks ────────────────────────────────────────────────────────────

export function useClassCheckins(sessionId: string, classId: string) {
  return useQuery({
    queryKey: ['class-checkins', sessionId, classId],
    queryFn: () => musterService.getClassCheckins(sessionId, classId).then((r) => r.data),
    enabled: !!sessionId && !!classId,
    refetchInterval: 5000,
  });
}

export function useUpdateStudentCheckin(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, data }: { studentId: string; data: UpdateStudentCheckinRequest }) =>
      musterService.updateStudentCheckin(sessionId, studentId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['muster-stats', sessionId] });
      qc.invalidateQueries({ queryKey: ['class-checkins', sessionId] });
      qc.invalidateQueries({ queryKey: ['muster-session', sessionId] });
    },
  });
}

export function useBulkStudentCheckin(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkStudentCheckinRequest) => musterService.bulkStudentCheckin(sessionId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['muster-stats', sessionId] });
      qc.invalidateQueries({ queryKey: ['class-checkins', sessionId] });
      qc.invalidateQueries({ queryKey: ['muster-session', sessionId] });
    },
  });
}

export function useUpdateStaffCheckin(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateStaffCheckinRequest }) =>
      musterService.updateStaffCheckin(sessionId, userId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['muster-stats', sessionId] });
      qc.invalidateQueries({ queryKey: ['muster-session', sessionId] });
    },
  });
}

export function useUpdateSweep(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ zoneId, data }: { zoneId: string; data: UpdateZoneSweepRequest }) =>
      musterService.updateSweep(sessionId, zoneId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['muster-stats', sessionId] });
      qc.invalidateQueries({ queryKey: ['muster-session', sessionId] });
    },
  });
}

// ── Tenant/Settings Hooks ────────────────────────────────────────────────────

export function useTenant() {
  return useQuery({
    queryKey: ['tenant'],
    queryFn: () => tenantService.get().then((r) => r.data),
  });
}

export function useTenantUsage() {
  return useQuery({
    queryKey: ['tenant-usage'],
    queryFn: () => tenantService.getUsage().then((r) => r.data),
  });
}

export function useTenantAIUsage() {
  return useQuery({
    queryKey: ['tenant-ai-usage'],
    queryFn: () => tenantService.getAIUsage().then((r) => r.data),
  });
}

// ── Billing Hooks ────────────────────────────────────────────────────────────

export function useSubscription() {
  return useQuery({
    queryKey: ['subscription'],
    queryFn: () => billingService.getSubscription().then((r) => r.data),
  });
}

export function useInvoices(query?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['invoices', query],
    queryFn: () => billingService.getInvoices(query).then((r) => r.data),
  });
}

// ── ERT Hooks ───────────────────────────────────────────────────────────────

export function useMyErtAssignments() {
  return useQuery({
    queryKey: ['ert', 'my-assignments'],
    queryFn: () => ertService.getMyAssignments().then((r) => r.data),
  });
}

export function useCoverageAssignments(query?: CoverageAssignmentsQuery) {
  return useQuery({
    queryKey: ['ert', 'coverage-assignments', query],
    queryFn: () => ertService.listCoverageAssignments(query).then((r) => r.data),
  });
}

export function useCreateCoverageAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCoverageAssignmentRequest) =>
      ertService.createCoverageAssignment(data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ert', 'coverage-assignments'] });
    },
  });
}

export function useDeleteCoverageAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ertService.deleteCoverageAssignment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ert', 'coverage-assignments'] });
    },
  });
}

export function useDelegations(query?: DelegationsQuery) {
  return useQuery({
    queryKey: ['ert', 'delegations', query],
    queryFn: () => ertService.listDelegations(query).then((r) => r.data),
  });
}

export function useApproveDelegation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ertService.approveDelegation(id).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ert', 'delegations'] });
      qc.invalidateQueries({ queryKey: ['ert', 'my-assignments'] });
    },
  });
}

export function useRejectDelegation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ertService.rejectDelegation(id).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ert', 'delegations'] });
    },
  });
}

// ── Notification Hooks ──────────────────────────────────────────────────────

export function useNotificationsUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsService.getUnreadCount().then((r) => r.data),
    refetchInterval: 30000,
  });
}

export function useNotificationsInbox(
  query?: InboxListQuery,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['notifications', 'inbox', query],
    queryFn: () => notificationsService.listInbox(query).then((r) => r.data),
    enabled: options?.enabled ?? true,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsService.markRead(id).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'inbox'] });
    },
  });
}

export function useReadAllNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsService.readAll().then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'inbox'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });
}

// ── RBAC Hooks ──────────────────────────────────────────────────────────────

export function useRbacPermissions() {
  return useQuery({
    queryKey: ['rbac', 'permissions'],
    queryFn: () => rbacService.listPermissions().then((r) => r.data),
  });
}

export function useRbacRoles() {
  return useQuery({
    queryKey: ['rbac', 'roles'],
    queryFn: () => rbacService.listRoles().then((r) => r.data),
  });
}

export function useRbacRole(id: string) {
  return useQuery({
    queryKey: ['rbac', 'role', id],
    queryFn: () => rbacService.getRoleById(id).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateRbacRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string; priority?: number; permission_keys: string[] }) =>
      rbacService.createRole(data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rbac', 'roles'] });
    },
  });
}

export function useUpdateRbacRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string | null; priority?: number; permission_keys?: string[] } }) =>
      rbacService.updateRole(id, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rbac', 'roles'] });
      qc.invalidateQueries({ queryKey: ['rbac', 'role'] });
    },
  });
}

export function useDeleteRbacRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) =>
      rbacService.deleteRole(id, { force }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rbac', 'roles'] });
    },
  });
}

export function useUserAssignments(userId: string) {
  return useQuery({
    queryKey: ['rbac', 'assignments', userId],
    queryFn: () => rbacService.listUserAssignments(userId).then((r) => r.data),
    enabled: !!userId,
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: CreateAssignmentRequest }) =>
      rbacService.createAssignment(userId, data).then((r) => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['rbac', 'assignments', variables.userId] });
    },
  });
}

export function useRevokeAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, assignmentId }: { userId: string; assignmentId: string }) =>
      rbacService.revokeAssignment(userId, assignmentId).then((r) => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['rbac', 'assignments', variables.userId] });
    },
  });
}

export function useSimulate() {
  return useMutation({
    mutationFn: (data: { user_id: string; permission_key: string; scope_type?: string; scope_id?: string }) =>
      rbacService.simulate(data).then((r) => r.data),
  });
}

export function useApprovalRequests(status?: string) {
  return useQuery({
    queryKey: ['rbac', 'approval-requests', status],
    queryFn: () => rbacService.listApprovalRequests(status).then((r) => r.data),
  });
}

export function useDecideApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { decision: 'approved' | 'rejected'; reason?: string } }) =>
      rbacService.decideApproval(id, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rbac', 'approval-requests'] });
    },
  });
}

export function useAccessReviewCampaigns() {
  return useQuery({
    queryKey: ['rbac', 'access-reviews'],
    queryFn: () => rbacService.listAccessReviewCampaigns().then((r) => r.data),
  });
}

export function useCreateAccessReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string; starts_at: string; ends_at: string }) =>
      rbacService.createAccessReview(data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rbac', 'access-reviews'] });
    },
  });
}

export function useCampaignAssignments(campaignId: string) {
  return useQuery({
    queryKey: ['rbac', 'access-reviews', campaignId, 'assignments'],
    queryFn: () => rbacService.listCampaignAssignments(campaignId).then((r) => r.data),
    enabled: !!campaignId,
  });
}

export function useSubmitAttestation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: { assignment_id: string; decision: 'confirm' | 'revoke' } }) =>
      rbacService.submitAttestation(campaignId, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rbac', 'access-reviews'] });
    },
  });
}

// ── Hub Hooks ────────────────────────────────────────────────────────────────

export function useHubFeed(query?: HubFeedQuery) {
  return useQuery({
    queryKey: ['hub-feed', query],
    queryFn: () => hubService.getFeed(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
    refetchInterval: 15_000,
  });
}

export function useHubWidgets(query?: HubWidgetQuery) {
  return useQuery({
    queryKey: ['hub-widgets', query],
    queryFn: () => hubService.getWidgets(query).then((r) => r.data),
    refetchInterval: 30_000,
  });
}

export function useHubEmsState(campusId: string) {
  return useQuery({
    queryKey: ['hub-ems', campusId],
    queryFn: () => hubService.getEmsState(campusId).then((r) => r.data),
    enabled: !!campusId,
    refetchInterval: 10_000,
  });
}

export function useHubInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: HubInteractionRequest) =>
      hubService.createInteraction(data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hub-feed'] });
    },
  });
}

// ── Dashboard Hooks ──────────────────────────────────────────────────────────

export function useDashboardData() {
  return useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboardService.getSummary().then((r) => r.data),
    refetchInterval: 60_000, // refresh every 60s
  });
}

// ── HSE Incident Hooks ────────────────────────────────────────────────────

export function useIncidents(query?: IncidentListQuery) {
  return useQuery({
    queryKey: ['hse', 'incidents', query],
    queryFn: () => hseService.listIncidents(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useIncident(id: string) {
  return useQuery({
    queryKey: ['hse', 'incident', id],
    queryFn: () => hseService.getIncident(id).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateIncidentRequest) => hseService.createIncident(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hse', 'incidents'] }); },
  });
}

export function useUpdateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateIncidentRequest }) => hseService.updateIncident(id, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'incidents'] });
      qc.invalidateQueries({ queryKey: ['hse', 'incident', vars.id] });
    },
  });
}

export function useAcknowledgeIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: TransitionReasonRequest }) => hseService.acknowledgeIncident(id, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'incidents'] });
      qc.invalidateQueries({ queryKey: ['hse', 'incident', vars.id] });
    },
  });
}

export function useStartInvestigation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: StartInvestigationRequest }) => hseService.startInvestigation(id, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'incidents'] });
      qc.invalidateQueries({ queryKey: ['hse', 'incident', vars.id] });
    },
  });
}

export function useSubmitForReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: TransitionReasonRequest }) => hseService.submitForReview(id, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'incidents'] });
      qc.invalidateQueries({ queryKey: ['hse', 'incident', vars.id] });
    },
  });
}

export function useCloseIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: TransitionReasonRequest }) => hseService.closeIncident(id, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'incidents'] });
      qc.invalidateQueries({ queryKey: ['hse', 'incident', vars.id] });
    },
  });
}

export function useReopenIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: TransitionReasonRequest }) => hseService.reopenIncident(id, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'incidents'] });
      qc.invalidateQueries({ queryKey: ['hse', 'incident', vars.id] });
    },
  });
}

export function useEscalateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: TransitionReasonRequest }) => hseService.escalateIncident(id, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'incidents'] });
      qc.invalidateQueries({ queryKey: ['hse', 'incident', vars.id] });
    },
  });
}

// ── HSE Injured Person Hooks ──────────────────────────────────────────────

export function useInjuredPersons(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'injured-persons', incidentId],
    queryFn: () => hseService.listInjuredPersons(incidentId).then((r) => r.data),
    enabled: !!incidentId,
  });
}

export function useAddInjuredPerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, data }: { incidentId: string; data: CreateInjuredPersonRequest }) => hseService.addInjuredPerson(incidentId, data).then((r) => r.data),
    onSuccess: (_data, vars) => { qc.invalidateQueries({ queryKey: ['hse', 'injured-persons', vars.incidentId] }); },
  });
}

export function useUpdateInjuredPerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, personId, data }: { incidentId: string; personId: string; data: UpdateInjuredPersonRequest }) => hseService.updateInjuredPerson(incidentId, personId, data).then((r) => r.data),
    onSuccess: (_data, vars) => { qc.invalidateQueries({ queryKey: ['hse', 'injured-persons', vars.incidentId] }); },
  });
}

export function useDeleteInjuredPerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, personId }: { incidentId: string; personId: string }) => hseService.deleteInjuredPerson(incidentId, personId),
    onSuccess: (_data, vars) => { qc.invalidateQueries({ queryKey: ['hse', 'injured-persons', vars.incidentId] }); },
  });
}

// ── HSE Witness Hooks ─────────────────────────────────────────────────────

export function useWitnesses(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'witnesses', incidentId],
    queryFn: () => hseService.listWitnesses(incidentId).then((r) => r.data),
    enabled: !!incidentId,
  });
}

export function useAddWitness() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, data }: { incidentId: string; data: CreateWitnessRequest }) => hseService.addWitness(incidentId, data).then((r) => r.data),
    onSuccess: (_data, vars) => { qc.invalidateQueries({ queryKey: ['hse', 'witnesses', vars.incidentId] }); },
  });
}

export function useUpdateWitness() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, witnessId, data }: { incidentId: string; witnessId: string; data: UpdateWitnessRequest }) => hseService.updateWitness(incidentId, witnessId, data).then((r) => r.data),
    onSuccess: (_data, vars) => { qc.invalidateQueries({ queryKey: ['hse', 'witnesses', vars.incidentId] }); },
  });
}

// ── HSE Finding Hooks ─────────────────────────────────────────────────────

export function useFindings(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'findings', incidentId],
    queryFn: () => hseService.listFindings(incidentId).then((r) => r.data),
    enabled: !!incidentId,
  });
}

export function useAddFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, data }: { incidentId: string; data: CreateFindingRequest }) => hseService.addFinding(incidentId, data).then((r) => r.data),
    onSuccess: (_data, vars) => { qc.invalidateQueries({ queryKey: ['hse', 'findings', vars.incidentId] }); },
  });
}

export function useUpdateFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, findingId, data }: { incidentId: string; findingId: string; data: UpdateFindingRequest }) => hseService.updateFinding(incidentId, findingId, data).then((r) => r.data),
    onSuccess: (_data, vars) => { qc.invalidateQueries({ queryKey: ['hse', 'findings', vars.incidentId] }); },
  });
}

// ── HSE Finding link hooks (INV-A-1 — read-only) ──────────────────────────
//
// Each hook is a thin read-only wrapper over an existing list endpoint.
// No mutations are introduced; linking and unlinking of cause-codes /
// barrier-codes / evidence to findings is reserved for a later
// investigation-write lane.
//
// Cache key shape follows the per-resource convention so a future
// invalidation lane can target a single finding's link set without
// touching the parent finding list cache.
//
// FINDINGS-DRAWER-READ-HARDEN-1: all three link hooks accept the parent
// `incidentId` and forward to the incident-scoped backend path
// (`/hse/incidents/:incidentId/findings/:findingId/...`). The optional
// `options.enabled` flag lets callers gate the query on drawer-expansion
// so collapsed finding cards never issue a background fetch. The cache
// key now embeds the parent incident id for all three hooks so the
// shape is uniform with the other incident-scoped read hooks below.

export function useFindingCauseCodeLinks(
  incidentId: string,
  findingId: string,
  options?: { enabled?: boolean },
) {
  const callerEnabled = options?.enabled !== false;
  return useQuery({
    queryKey: [
      'hse',
      'incident',
      incidentId,
      'finding',
      findingId,
      'cause-code-links',
    ],
    queryFn: () =>
      hseService
        .listFindingCauseCodes(incidentId, findingId)
        .then((r) => r.data),
    enabled: callerEnabled && !!incidentId && !!findingId,
    staleTime: 30_000,
  });
}

export function useFindingBarrierCodeLinks(
  incidentId: string,
  findingId: string,
  options?: { enabled?: boolean },
) {
  const callerEnabled = options?.enabled !== false;
  return useQuery({
    queryKey: [
      'hse',
      'incident',
      incidentId,
      'finding',
      findingId,
      'barrier-code-links',
    ],
    queryFn: () =>
      hseService
        .listFindingBarrierCodes(incidentId, findingId)
        .then((r) => r.data),
    enabled: callerEnabled && !!incidentId && !!findingId,
    staleTime: 30_000,
  });
}

export function useFindingEvidenceLinks(
  incidentId: string,
  findingId: string,
  options?: { enabled?: boolean },
) {
  const callerEnabled = options?.enabled !== false;
  return useQuery({
    queryKey: [
      'hse',
      'incident',
      incidentId,
      'finding',
      findingId,
      'evidence-links',
    ],
    queryFn: () =>
      hseService
        .listFindingEvidenceLinks(incidentId, findingId)
        .then((r) => r.data),
    enabled: callerEnabled && !!incidentId && !!findingId,
    staleTime: 30_000,
  });
}

// ── HSE RCA read hooks (INV-A-2A — read-only) ─────────────────────────────
//
// Read-only query hooks over existing service GETs. No mutations. No new
// service methods. Cache keys are stable and scoped per the
// ['hse','incident',id,'<resource>'] convention so future invalidation
// lanes can target a single artefact set without touching siblings.
//
// Per-chain / per-diagram child reads MUST be invoked inside child
// components (e.g. one FiveWhysSteps render per chain), never inside a
// parent loop — that would violate React's rules-of-hooks.

export function useFiveWhysChains(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'incident', incidentId, 'five-whys-chains'],
    queryFn: () => hseService.listFiveWhysChains(incidentId).then((r) => r.data),
    enabled: !!incidentId,
    staleTime: 30_000,
  });
}

export function useFiveWhysSteps(incidentId: string, chainId: string) {
  return useQuery({
    queryKey: [
      'hse',
      'incident',
      incidentId,
      'five-whys-chain',
      chainId,
      'steps',
    ],
    queryFn: () =>
      hseService.listFiveWhysSteps(incidentId, chainId).then((r) => r.data),
    enabled: !!incidentId && !!chainId,
    staleTime: 30_000,
  });
}

export function useFishbones(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'incident', incidentId, 'fishbones'],
    queryFn: () => hseService.listFishbones(incidentId).then((r) => r.data),
    enabled: !!incidentId,
    staleTime: 30_000,
  });
}

export function useFishboneBranches(incidentId: string, diagramId: string) {
  return useQuery({
    queryKey: [
      'hse',
      'incident',
      incidentId,
      'fishbone',
      diagramId,
      'branches',
    ],
    queryFn: () =>
      hseService
        .listFishboneBranches(incidentId, diagramId)
        .then((r) => r.data),
    enabled: !!incidentId && !!diagramId,
    staleTime: 30_000,
  });
}

export function useTimelineEvents(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'incident', incidentId, 'rca-timeline'],
    queryFn: () =>
      hseService.listTimelineEvents(incidentId).then((r) => r.data),
    enabled: !!incidentId,
    staleTime: 30_000,
  });
}

export function useBarrierAnalyses(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'incident', incidentId, 'barrier-analyses'],
    queryFn: () =>
      hseService.listBarrierAnalyses(incidentId).then((r) => r.data),
    enabled: !!incidentId,
    staleTime: 30_000,
  });
}

// ── HSE Evidence Hooks ────────────────────────────────────────────────────

export function useEvidence(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'evidence', incidentId],
    queryFn: () => hseService.listEvidence(incidentId).then((r) => r.data),
    enabled: !!incidentId,
  });
}

export function useAddEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, data }: { incidentId: string; data: CreateEvidenceRequest }) => hseService.addEvidence(incidentId, data).then((r) => r.data),
    onSuccess: (_data, vars) => { qc.invalidateQueries({ queryKey: ['hse', 'evidence', vars.incidentId] }); },
  });
}

// PR4.5: real file upload mutation. Caller passes a File from <input type="file">.
export function useUploadIncidentEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      incidentId,
      file,
      evidenceType,
      description,
    }: {
      incidentId: string;
      file: File;
      evidenceType: string;
      description?: string | null;
    }) =>
      hseService
        .uploadIncidentEvidence(incidentId, { file, evidenceType, description })
        .then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'evidence', vars.incidentId] });
    },
  });
}

// ── HSE Near Miss Hooks ───────────────────────────────────────────────────

export function useNearMisses(query?: NearMissListQuery) {
  return useQuery({
    queryKey: ['hse', 'near-misses', query],
    queryFn: () => hseService.listNearMisses(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useNearMiss(id: string) {
  return useQuery({
    queryKey: ['hse', 'near-miss', id],
    queryFn: () => hseService.getNearMiss(id).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateNearMiss() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateNearMissRequest) => hseService.createNearMiss(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hse', 'near-misses'] }); },
  });
}

export function useUpdateNearMiss() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateNearMissRequest }) => hseService.updateNearMiss(id, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'near-misses'] });
      qc.invalidateQueries({ queryKey: ['hse', 'near-miss', vars.id] });
    },
  });
}

export function useReviewNearMiss() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReviewNearMissRequest }) => hseService.reviewNearMiss(id, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'near-misses'] });
      qc.invalidateQueries({ queryKey: ['hse', 'near-miss', vars.id] });
    },
  });
}

export function useConvertNearMiss() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: import('@amuster/types').ConvertNearMissRequest }) => hseService.convertNearMiss(id, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hse', 'near-misses'] });
      qc.invalidateQueries({ queryKey: ['hse', 'incidents'] });
    },
  });
}

// ── HSE CAPA Hooks ──────────────────────────────────────────────────────

export function useCapas(query?: CapaListQuery) {
  return useQuery({
    queryKey: ['hse', 'capas', query],
    queryFn: () => hseService.listCapas(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useCapaSummary() {
  return useQuery({
    queryKey: ['hse', 'capas', 'summary'],
    queryFn: () => hseService.getCapaSummary().then((r) => r.data),
    // Tiles sit above the list — keep them fresh but don't pound the endpoint.
    staleTime: 30_000,
  });
}

// Active CAPA = status NOT IN ('completed', 'cancelled'). The
// /corrective-actions/summary endpoint only ships `open` (status='open'),
// which under-reports the true active workload because in_progress,
// pending_verification, verified, and pending_effectiveness_review are all
// active per the accepted contract. This hook fans out N status-filtered
// `limit: 1` count queries against the existing listCapas endpoint and sums
// `meta.total`. No backend change, no new endpoint, no mutation.
//
// Non-terminal CAPA statuses per packages/types/src/enums.ts CAPA_STATUSES
// (terminal = 'completed', 'cancelled'):
const ACTIVE_CAPA_STATUSES: ReadonlyArray<CapaStatus> = [
  'open',
  'in_progress',
  'pending_verification',
  'verified',
  'pending_effectiveness_review',
];

export function useActiveCapaCount() {
  const queries = useQueries({
    queries: ACTIVE_CAPA_STATUSES.map((status) => ({
      queryKey: ['hse', 'capas', 'active-count', status] as const,
      queryFn: () =>
        hseService
          .listCapas({ page: 1, limit: 1, status })
          .then((r) => r.data.meta.total),
      staleTime: 30_000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const error = queries.find((q) => q.error)?.error ?? null;
  // While any sub-query is still loading, fall back to 0 for the partial sum.
  // The consumer should gate display on isLoading rather than show a stale
  // intermediate count.
  const count = queries.reduce((sum, q) => sum + (q.data ?? 0), 0);

  return { count, isLoading, isError, error };
}

export function useIncidentCapas(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'capas', 'incident', incidentId],
    queryFn: () => hseService.listIncidentCapas(incidentId).then((r) => r.data),
    enabled: !!incidentId,
  });
}

export function useCapa(capaId: string) {
  return useQuery({
    queryKey: ['hse', 'capa', capaId],
    queryFn: () => hseService.getCapa(capaId).then((r) => r.data),
    enabled: !!capaId,
  });
}

export function useCapaActivity(capaId: string, enabled = true) {
  return useQuery({
    queryKey: ['hse', 'capa', capaId, 'activity'],
    queryFn: () => hseService.listCapaActivity(capaId).then((r) => r.data),
    enabled: !!capaId && enabled,
  });
}

// ── CAPA Evidence (PR2) ────────────────────────────────────────────────

export function useCapaEvidence(capaId: string, enabled = true) {
  return useQuery({
    queryKey: ['hse', 'capa', capaId, 'evidence'],
    queryFn: () => hseService.listCapaEvidence(capaId).then((r) => r.data),
    enabled: !!capaId && enabled,
  });
}

export function useAttachCapaEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ capaId, data }: { capaId: string; data: AttachCapaEvidenceRequest }) =>
      hseService.attachCapaEvidence(capaId, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'capa', vars.capaId] });
    },
  });
}

export function useRemoveCapaEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ capaId, capaEvidenceId }: { capaId: string; capaEvidenceId: string }) =>
      hseService.removeCapaEvidence(capaId, capaEvidenceId).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'capa', vars.capaId] });
    },
  });
}

// ── CAPA Comments (PR2) ────────────────────────────────────────────────

export function useCapaComments(capaId: string, enabled = true) {
  return useQuery({
    queryKey: ['hse', 'capa', capaId, 'comments'],
    queryFn: () => hseService.listCapaComments(capaId).then((r) => r.data),
    enabled: !!capaId && enabled,
  });
}

export function useAddCapaComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ capaId, data }: { capaId: string; data: AddCapaCommentRequest }) =>
      hseService.addCapaComment(capaId, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'capa', vars.capaId] });
    },
  });
}

export function useEditCapaComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      capaId,
      commentId,
      data,
    }: {
      capaId: string;
      commentId: string;
      data: EditCapaCommentRequest;
    }) => hseService.editCapaComment(capaId, commentId, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'capa', vars.capaId] });
    },
  });
}

export function useDeleteCapaComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ capaId, commentId }: { capaId: string; commentId: string }) =>
      hseService.deleteCapaComment(capaId, commentId).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'capa', vars.capaId] });
    },
  });
}

export function useCreateCapa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, data }: { incidentId: string; data: CreateCapaRequest }) => hseService.createCapa(incidentId, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'capas'] });
      qc.invalidateQueries({ queryKey: ['hse', 'capas', 'incident', vars.incidentId] });
    },
  });
}

export function useUpdateCapa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ capaId, data }: { capaId: string; data: UpdateCapaRequest }) => hseService.updateCapa(capaId, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'capas'] });
      qc.invalidateQueries({ queryKey: ['hse', 'capa', vars.capaId] });
    },
  });
}

export function useAssignCapa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ capaId, data }: { capaId: string; data: AssignCapaRequest }) => hseService.assignCapa(capaId, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'capas'] });
      qc.invalidateQueries({ queryKey: ['hse', 'capa', vars.capaId] });
    },
  });
}

export function useStartCapaProgress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (capaId: string) => hseService.startCapaProgress(capaId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hse', 'capas'] });
    },
  });
}

export function useCompleteCapa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (capaId: string) => hseService.completeCapa(capaId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hse', 'capas'] });
    },
  });
}

export function useVerifyCapa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ capaId, data }: { capaId: string; data: VerifyCapaRequest }) => hseService.verifyCapa(capaId, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'capas'] });
      qc.invalidateQueries({ queryKey: ['hse', 'capa', vars.capaId] });
    },
  });
}

// PR7: submit an effectiveness-review outcome for a CAPA. Valid from
// status ∈ { 'verified', 'pending_effectiveness_review' }.
export function useSubmitCapaEffectivenessReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      capaId,
      data,
    }: {
      capaId: string;
      data: SubmitEffectivenessReviewRequest;
    }) =>
      hseService.submitEffectivenessReview(capaId, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'capas'] });
      qc.invalidateQueries({ queryKey: ['hse', 'capa', vars.capaId] });
    },
  });
}

export function useCancelCapa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ capaId, data }: { capaId: string; data?: CancelCapaRequest }) => hseService.cancelCapa(capaId, data).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['hse', 'capas'] });
      qc.invalidateQueries({ queryKey: ['hse', 'capa', vars.capaId] });
    },
  });
}

// ── Taxonomy (Cause / Barrier Codes) ────────────────────────────────────────

export function useCauseCodes(query?: { limit?: number; is_active?: boolean }) {
  return useQuery({
    queryKey: ['hse', 'cause-codes', query],
    queryFn: () => hseService.listCauseCodes(query).then((r) => r.data),
  });
}

export function useBarrierCodes(query?: { limit?: number; is_active?: boolean }) {
  return useQuery({
    queryKey: ['hse', 'barrier-codes', query],
    queryFn: () => hseService.listBarrierCodes(query).then((r) => r.data),
  });
}

// ── Safety Bulletins ────────────────────────────────────────────────────────

export function useBulletins(query?: { page?: number; limit?: number; status?: string; severity?: string }) {
  return useQuery({
    queryKey: ['hse', 'bulletins', query],
    queryFn: () => hseService.listBulletins(query).then((r) => r.data),
  });
}

/**
 * Singular bulletin GET. Disabled when bulletinId is undefined so callers
 * can pass route params before they are resolved without firing a request.
 *
 * Cache key is `['hse', 'bulletin', bulletinId]` (singular) — distinct from
 * the list-cache key `['hse', 'bulletins', query]` (plural). Mutations
 * invalidate both.
 */
export function useBulletin(bulletinId: string | undefined) {
  return useQuery({
    queryKey: ['hse', 'bulletin', bulletinId],
    queryFn: () => hseService.getBulletin(bulletinId as string).then((r) => r.data),
    enabled: !!bulletinId,
  });
}

/**
 * Bulletin source-bridge query. Returns the resolver-projected source
 * displays for a bulletin (typically 0 or 1 row in v1; supports N>1 in
 * future). Disabled when bulletinId is undefined.
 *
 * Cache key is `['hse', 'bulletin-sources', bulletinId]`. The bulletin
 * update mutation does NOT invalidate this key — sources are only mutated
 * at bulletin creation time when an `incident_id` is supplied AND the
 * dual-write feature flag resolves true; subsequent edits to title/status
 * do not touch the bridge.
 */
export function useBulletinSources(bulletinId: string | undefined) {
  return useQuery({
    queryKey: ['hse', 'bulletin-sources', bulletinId],
    queryFn: () =>
      hseService.getBulletinSources(bulletinId as string).then((r) => r.data),
    enabled: !!bulletinId,
  });
}

export function useCreateBulletin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => hseService.createBulletin(data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hse', 'bulletins'] });
    },
  });
}

export function useUpdateBulletin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bulletinId, data }: { bulletinId: string; data: Record<string, unknown> }) => hseService.updateBulletin(bulletinId, data).then((r) => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['hse', 'bulletins'] });
      qc.invalidateQueries({ queryKey: ['hse', 'bulletin', variables.bulletinId] });
    },
  });
}

// ── Management Reviews (mig 089) ────────────────────────────────────────────
//
// PR #546 shipped the read-only hooks below (list / detail / sources / items).
// PR #548 (this) adds the create + update item mutations consumed by
// `ManagementReviewItemFormModal`. Both mutation hooks invalidate exactly
// two keys: the items list (`['hse','management-review-items', reviewId]`)
// and the parent review (`['hse','management-review', reviewId]`) since
// the KPI strip derives totals from the items query result. No other
// HSE / workflow keys are invalidated.

export function useManagementReviews(query?: {
  page?: number;
  limit?: number;
  status?: string;
  chair_user_id?: string;
  facilitator_user_id?: string;
}) {
  return useQuery({
    queryKey: ['hse', 'management-reviews', query ?? {}],
    queryFn: () => hseService.listManagementReviews(query).then((r) => r.data),
    staleTime: 30_000,
  });
}

export function useManagementReview(reviewId: string | undefined) {
  return useQuery({
    queryKey: ['hse', 'management-review', reviewId],
    queryFn: () =>
      hseService.getManagementReview(reviewId as string).then((r) => r.data),
    enabled: !!reviewId,
    staleTime: 30_000,
  });
}

export function useManagementReviewSources(reviewId: string | undefined) {
  return useQuery({
    queryKey: ['hse', 'management-review-sources', reviewId],
    queryFn: () =>
      hseService
        .getManagementReviewSources(reviewId as string)
        .then((r) => r.data),
    enabled: !!reviewId,
    staleTime: 30_000,
  });
}

export function useManagementReviewItems(
  reviewId: string | undefined,
  query?: { page?: number; limit?: number; status?: string; item_type?: string },
) {
  return useQuery({
    queryKey: ['hse', 'management-review-items', reviewId, query ?? {}],
    queryFn: () =>
      hseService
        .getManagementReviewItems(reviewId as string, query)
        .then((r) => r.data),
    enabled: !!reviewId,
    staleTime: 30_000,
  });
}

// PR #548 — create item.
// Mutation body shape comes from `serializeCreateManagementReviewItemPayload`;
// callers never construct the wire payload by hand.
export function useCreateManagementReviewItem(reviewId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateManagementReviewItemPayload) =>
      hseService
        .createManagementReviewItem(reviewId as string, body)
        .then((r) => r.data),
    onSuccess: () => {
      // Invalidate exactly the two keys the modal can affect — items list
      // (any filtered variant under the same prefix is also refreshed by
      // react-query's prefix-matching) and the parent review (KPI strip).
      qc.invalidateQueries({
        queryKey: ['hse', 'management-review-items', reviewId],
      });
      qc.invalidateQueries({
        queryKey: ['hse', 'management-review', reviewId],
      });
    },
  });
}

// PR #548 — update item.
// Body comes from `serializePatchManagementReviewItemPayload` — diff-only.
export function useUpdateManagementReviewItem(reviewId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      itemId: string;
      body: UpdateManagementReviewItemPayload;
    }) =>
      hseService
        .updateManagementReviewItem(reviewId as string, vars.itemId, vars.body)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['hse', 'management-review-items', reviewId],
      });
      qc.invalidateQueries({
        queryKey: ['hse', 'management-review', reviewId],
      });
    },
  });
}

// ── Lessons Learned (incident-scoped) ───────────────────────────────────────
//
// PR #550 — adds the read-only query hook consumed by IncidentLessonsPanel.
// Backend route `GET /api/hse/incidents/:id/lessons` is gated by
// `hse:lessons:view` and returns lessons in `created_at DESC` order
// (`LessonLearnedService.list`). The panel renders rows in API order
// without client-side sorting.
//
// No mutation hook in this PR — create/update/delete remain accessible
// via `hseService.create/update/deleteLesson` for callers that already
// use them, but no new wiring or UI exposes mutations from this PR.

export function useIncidentLessons(incidentId: string | undefined) {
  return useQuery({
    queryKey: ['hse', 'incident-lessons', incidentId],
    queryFn: () =>
      hseService.listLessons(incidentId as string).then((r) => r.data),
    enabled: !!incidentId,
    staleTime: 30_000,
  });
}

// PR #553 — tenant-wide Lessons Learned Register hook.
// Backend route: GET /api/hse/incidents/lessons (literal-segment, distinct
// from the incident-scoped GET /:id/lessons). Backend already returns
// `created_at DESC`; the consumer renders rows in API order with no
// client-side sort. Filters mirror the existing
// `listLessonsLearnedQuerySchema` — incident_id / status / category /
// pagination — no `search` field in v1.
export function useLessonsRegister(query?: {
  page?: number;
  limit?: number;
  incident_id?: string;
  status?: string;
  category?: string;
}) {
  return useQuery({
    queryKey: ['hse', 'lessons-register', query ?? {}],
    queryFn: () => hseService.listLessonsRegister(query).then((r) => r.data),
    staleTime: 30_000,
  });
}

// PR A — Incident Lifecycle Readiness (read-only advisory).
// Fetches the G1–G7 gate readiness signal for a single incident. This
// hook is INTENTIONALLY query-only: it does not mutate, it does not
// invalidate any other key, and it has no side-effects. The readiness
// signal is advisory; nothing in the UI gates on it.
export function useIncidentLifecycleReadiness(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'incident', incidentId, 'lifecycle'],
    queryFn: () =>
      hseService.getIncidentLifecycleReadiness(incidentId).then((r) => r.data),
    enabled: !!incidentId,
    staleTime: 30_000,
  });
}

// PR INV-TEAM-A1 — Investigation Team Mobilization (read-only).
// Calls GET /api/hse/incidents/:id/investigation-team. Returns the
// active members of the investigation team for one incident plus a
// removed_count. INTENTIONALLY query-only: no mutation, no cache
// invalidation triggers, no navigation side effect.
export function useInvestigationTeam(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'incident', incidentId, 'investigation-team'],
    queryFn: () =>
      hseService.getInvestigationTeam(incidentId).then((r) => r.data),
    enabled: !!incidentId,
    staleTime: 30_000,
  });
}

// PR INV-STATEMENT-CONSENT-A2-2 — Statements & Consent (read-only).
// Calls GET /api/hse/incidents/:id/statement-consent. Returns the
// compact data-minimized overview of participants / consent records /
// statement records for one incident, plus three removed_*_count
// aggregates. INTENTIONALLY query-only: no mutation, no cache
// invalidation triggers, no navigation side effect.
export function useStatementConsent(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'incident', incidentId, 'statement-consent'],
    queryFn: () =>
      hseService.getStatementConsent(incidentId).then((r) => r.data),
    enabled: !!incidentId,
    staleTime: 30_000,
  });
}

// PR OPM-B — Incident Operational Memory (read-only Operator Evidence
// Ledger consumer). Fetches the read-model aggregated by PR OPM-A1's
// sealed endpoint GET /api/hse/incidents/:id/operational-memory.
// INTENTIONALLY query-only: no mutation, no cache invalidation
// triggers, no navigation side effect. The ledger is observational,
// not enforcement.
export function useIncidentOperationalMemory(incidentId: string) {
  return useQuery({
    queryKey: ['hse', 'incident', incidentId, 'operational-memory'],
    queryFn: () =>
      hseService.getIncidentOperationalMemory(incidentId).then((r) => r.data),
    enabled: !!incidentId,
    staleTime: 30_000,
  });
}

// PR D-UI — Work-Relatedness Determination (append-only governed write).
// POSTs a single determination row through the sealed PR #566 endpoint and
// invalidates ONLY the lifecycle-readiness cache key for this incident so
// the panel re-fetches the updated G1. No navigation side effect. No
// status / close / workflow / source-bridge / lessons-lifecycle activity.
export function useCreateWorkRelatednessDetermination(incidentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      determination:
        | 'work_related'
        | 'not_work_related'
        | 'undetermined'
        | 'not_applicable';
      rationale?: string | null;
      determination_context_code?: string | null;
    }) =>
      hseService
        .createWorkRelatednessDetermination(incidentId, data)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['hse', 'incident', incidentId, 'lifecycle'],
      });
    },
  });
}

// ── Regulatory Reporting ────────────────────────────────────────────────────

export function useRegulatoryRules(query?: { limit?: number }) {
  return useQuery({
    queryKey: ['hse', 'regulatory-rules', query],
    queryFn: () => hseService.listRegulatoryRules(query).then((r) => r.data),
  });
}

// ── Export Hooks ─────────────────────────────────────────────────────────────

export function useExports(query?: ExportListQuery) {
  return useQuery({
    queryKey: ['exports', query],
    queryFn: () => exportService.list(query).then((r) => r.data),
    enabled: query !== undefined,
    placeholderData: (previousData) => previousData,
  });
}

export function useCreateExport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateExportRequest) => exportService.create(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['exports'] }); },
  });
}

export function useCancelExport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => exportService.cancel(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['exports'] }); },
  });
}

// ── User Preferences Hooks ──────────────────────────────────────────────────

export function useUserPreferences() {
  return useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => preferencesService.getPreferences().then((r) => r.data),
  });
}

export function useUpdateUserPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => preferencesService.updatePreferences(data as any).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-preferences'] }); },
  });
}

// ── HSE Approval Delegation Hooks ────────────────────────────────────────────

export function useApprovalDelegations(query?: { page?: number; limit?: number; scope?: string; is_active?: boolean }) {
  return useQuery({
    queryKey: ['hse', 'approval-delegations', query],
    queryFn: () => hseService.listDelegations(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useCreateApprovalDelegation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => hseService.createDelegation(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hse', 'approval-delegations'] }); },
  });
}

export function useRevokeApprovalDelegation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: { reason?: string } }) => hseService.revokeDelegation(id, data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hse', 'approval-delegations'] }); },
  });
}

// ── HSE Exposure Hours Hooks ────────────────────────────────────────────────

export function useExposureHours(query?: { page?: number; limit?: number; campus_id?: string; period_type?: string }) {
  return useQuery({
    queryKey: ['hse', 'exposure-hours', query],
    queryFn: () => hseService.listExposureHours(query).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });
}

export function useCreateExposureHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => hseService.createExposureHours(data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hse', 'exposure-hours'] }); },
  });
}

export function useUpdateExposureHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => hseService.updateExposureHours(id, data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hse', 'exposure-hours'] }); },
  });
}

export function useDeleteExposureHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hseService.deleteExposureHours(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hse', 'exposure-hours'] }); },
  });
}

// ── HSE Quality Gates Hooks ─────────────────────────────────────────────────

export function useQualityGates(query?: { page?: number; limit?: number; applies_to_transition?: string; is_active?: boolean }) {
  return useQuery({
    queryKey: ['hse', 'quality-gates', query],
    queryFn: () => hseService.listQualityGates(query).then((r) => r.data),
  });
}

// ── RBAC Effective Access & History Hooks ─────────────────────────────────────

export function useEffectiveAccess(userId: string) {
  return useQuery({
    queryKey: ['rbac', 'effective-access', userId],
    queryFn: () => rbacService.getEffectiveAccess(userId).then((r) => r.data),
    enabled: !!userId,
    // Don't retry on 403 — user simply lacks rbac:view permission.
    // Retrying won't help; the sidebar falls back to role-based filtering
    // via empty permissionSet (useNavigation.ts:86).
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 403) return false;
      return failureCount < 3;
    },
  });
}

export function useAccessHistory(userId: string) {
  return useQuery({
    queryKey: ['rbac', 'access-history', userId],
    queryFn: () => rbacService.getAccessHistory(userId).then((r) => r.data),
    enabled: !!userId,
  });
}
